import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  Chip,
  Alert,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import SendIcon from '@mui/icons-material/Send';
import {
  useGetNotificationCampaignQuery,
  useCreateNotificationCampaignMutation,
  useUpdateNotificationCampaignMutation,
  useScheduleNotificationCampaignMutation,
  useGetNotificationTemplatesQuery,
  useGetNotificationChannelsQuery,
  useGetNotificationGroupsQuery,
} from '../../services/connectApi';
import { 
  NotificationCampaign, 
  NotificationTemplate
} from '../../models';
import UserAutocomplete from '../../components/UserAutocomplete';

const CampaignForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [campaign, setCampaign] = useState<Partial<NotificationCampaign>>({
    name: '',
    description: '',
    template: '',
    channels: [],
    groups: [],
    individual_users: [],
    status: 'draft',
    template_variables: {},
  });
  
  const [scheduledDate, setScheduledDate] = useState<Date | null>(null);
  const [sendNow, setSendNow] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);

  const { data: existingCampaign } = useGetNotificationCampaignQuery(
    id as string,
    { skip: !isEditMode }
  );
  
  const { data: templatesData, isLoading: templatesLoading } = useGetNotificationTemplatesQuery({page: 1, page_size: 100});
  const templates = templatesData?.results;
  const { data: channelsData, isLoading: channelsLoading } = useGetNotificationChannelsQuery({page: 1, page_size: 100});
  const channels = channelsData?.results;
  const { data: groupsData, isLoading: groupsLoading } = useGetNotificationGroupsQuery({page: 1, page_size: 100});
  const groups = groupsData?.results;
  
  const [createCampaign] = useCreateNotificationCampaignMutation();
  const [updateCampaign] = useUpdateNotificationCampaignMutation();
  const [scheduleCampaign] = useScheduleNotificationCampaignMutation();

  useEffect(() => {
    if (isEditMode && existingCampaign) {
      setCampaign(existingCampaign);
      
      if (existingCampaign.scheduled_at) {
        setScheduledDate(new Date(existingCampaign.scheduled_at));
      }
    }
  }, [isEditMode, existingCampaign]);

  useEffect(() => {
    if (templates && campaign.template) {
      const template = templates.find(t => t.id === campaign.template);
      setSelectedTemplate(template || null);
      
      // Initialize template variables if not already set
      if (template && template.variables.length > 0 && 
          (!campaign.template_variables || Object.keys(campaign.template_variables).length === 0)) {
        const initialVariables: Record<string, string> = {};
        template.variables.forEach(variable => {
          initialVariables[variable] = '';
        });
        setCampaign(prev => ({
          ...prev,
          template_variables: initialVariables
        }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates, campaign.template]);

  const handleInputChange = (field: keyof NotificationCampaign, value: any) => {
    setCampaign((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field if it exists
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleTemplateChange = (templateId: string) => {
    handleInputChange('template', templateId);
    
    // Reset template variables when template changes
    setCampaign(prev => ({
      ...prev,
      template_variables: {}
    }));
  };

  const handleVariableChange = (variable: string, value: string) => {
    setCampaign(prev => ({
      ...prev,
      template_variables: {
        ...(prev.template_variables || {}),
        [variable]: value
      }
    }));
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!campaign.name) {
      newErrors.name = 'Name is required';
    }
    
    if (!campaign.template) {
      newErrors.template = 'Template is required';
    }
    
    if (!campaign.channels || campaign.channels.length === 0) {
      newErrors.channels = 'At least one channel is required';
    }
    
    // Check if at least one recipient group or individual user is selected
    const hasGroups = campaign.groups && campaign.groups.length > 0;
    const hasUsers = campaign.individual_users && campaign.individual_users.length > 0;
    
    if (!hasGroups && !hasUsers) {
      newErrors.recipients = 'You must select at least one group or individual user';
    }
    
    // Validate template variables if a template is selected
    if (selectedTemplate && selectedTemplate.variables.length > 0) {
      selectedTemplate.variables.forEach(variable => {
        if (!campaign.template_variables || !campaign.template_variables[variable]) {
          newErrors[`variable_${variable}`] = `${variable} is required`;
        }
      });
    }
    
    // If sending now or scheduling, validate the schedule date
    if (sendNow) {
      // No validation needed for sending now
    } else if (campaign.status === 'scheduled' || isEditMode) {
      if (!scheduledDate) {
        newErrors.scheduledDate = 'Schedule date is required';
      } else if (scheduledDate < new Date()) {
        newErrors.scheduledDate = 'Schedule date must be in the future';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (schedule: boolean) => {
    setSendNow(schedule);
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      let campaignId = id;
      let campaignData = {...campaign};
      
      if (schedule) {
        campaignData.status = 'scheduled';
        if (scheduledDate) {
          campaignData.scheduled_at = scheduledDate.toISOString();
        }
      }
      
      if (isEditMode) {
        await updateCampaign({
          id: id as string,
          data: campaignData,
        }).unwrap();
      } else {
        const result = await createCampaign(campaignData).unwrap();
        campaignId = result.id;
      }
      
      if (schedule && campaignId) {
        await scheduleCampaign({
          id: campaignId,
          scheduledAt: scheduledDate ? scheduledDate.toISOString() : undefined,
        }).unwrap();
      }
      
      navigate('/connect');
    } catch (error) {
      console.error('Failed to save or schedule campaign:', error);
      setErrors({
        submit: 'Failed to save campaign. Please try again.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isLoading = templatesLoading || channelsLoading || groupsLoading;
  const isCampaignEditable = !isEditMode || (existingCampaign && existingCampaign.status === 'draft');

  if (isEditMode && isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/connect')}
          sx={{ mr: 2 }}
        >
          Back
        </Button>
        <Typography variant="h5" component="h1">
          {isEditMode ? 'Edit Campaign' : 'Create Campaign'}
        </Typography>
      </Box>

      {errors.submit && (
        <Box sx={{ bgcolor: 'error.light', color: 'error.contrastText', p: 2, borderRadius: 1, mb: 3 }}>
          <Typography variant="body1">{errors.submit}</Typography>
        </Box>
      )}

      {isEditMode && existingCampaign && existingCampaign.status !== 'draft' && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          This campaign is in '{existingCampaign.status}' status and cannot be fully edited. You can view details and make limited changes.
        </Alert>
      )}

      <Paper elevation={3}>
        <Box sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Campaign Name"
                fullWidth
                required
                value={campaign.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                error={!!errors.name}
                helperText={errors.name || ''}
                disabled={!isCampaignEditable}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Description"
                fullWidth
                value={campaign.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                disabled={!isCampaignEditable}
              />
            </Grid>
          </Grid>
        </Box>

        <Divider />

        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Notification Content
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControl fullWidth required error={!!errors.template} disabled={!isCampaignEditable}>
                <InputLabel id="template-select-label">Notification Template</InputLabel>
                <Select
                  labelId="template-select-label"
                  value={campaign.template || ''}
                  label="Notification Template"
                  onChange={(e) => handleTemplateChange(e.target.value as string)}
                >
                  <MenuItem value="" disabled>
                    Select a template
                  </MenuItem>
                  {templates?.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      {template.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.template && <FormHelperText>{errors.template}</FormHelperText>}
              </FormControl>
            </Grid>
            
            {selectedTemplate && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle1" gutterBottom>
                      Template Preview
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Push Title:
                          </Typography>
                          <Typography variant="body1">
                            {selectedTemplate.push_title || 'No title specified'}
                          </Typography>
                        </Box>
                        
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Push Body:
                          </Typography>
                          <Typography variant="body1">
                            {selectedTemplate.push_body || 'No body specified'}
                          </Typography>
                        </Box>
                      </Grid>
                      
                      <Grid item xs={12} md={6}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body2" color="text.secondary">
                            Email Subject:
                          </Typography>
                          <Typography variant="body1">
                            {selectedTemplate.email_subject || 'No subject specified'}
                          </Typography>
                        </Box>
                        
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            SMS:
                          </Typography>
                          <Typography variant="body1">
                            {selectedTemplate.sms_body || 'No SMS body specified'}
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}
            
            {selectedTemplate && selectedTemplate.variables.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Template Variables
                </Typography>
                <Grid container spacing={2}>
                  {selectedTemplate.variables.map((variable) => (
                    <Grid item xs={12} md={6} key={variable}>
                      <TextField
                        label={variable}
                        fullWidth
                        value={campaign.template_variables?.[variable] || ''}
                        onChange={(e) => handleVariableChange(variable, e.target.value)}
                        error={!!errors[`variable_${variable}`]}
                        helperText={errors[`variable_${variable}`] || ''}
                        disabled={!isCampaignEditable}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            )}
          </Grid>
        </Box>

        <Divider />

        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Delivery Channels
          </Typography>
          
          <FormControl fullWidth required error={!!errors.channels} disabled={!isCampaignEditable}>
            <InputLabel id="channels-select-label">Notification Channels</InputLabel>
            <Select
              labelId="channels-select-label"
              multiple
              value={campaign.channels || []}
              label="Notification Channels"
              onChange={(e) => handleInputChange('channels', e.target.value as string[])}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const channel = channels?.find(c => c.id === value);
                    return channel ? (
                      <Chip key={value} label={channel.name} />
                    ) : null;
                  })}
                </Box>
              )}
            >
              {channels?.filter(channel => channel.is_active).map((channel) => (
                <MenuItem key={channel.id} value={channel.id}>
                  {channel.name} ({channel.type})
                </MenuItem>
              ))}
            </Select>
            {errors.channels && <FormHelperText>{errors.channels}</FormHelperText>}
          </FormControl>
        </Box>

        <Divider />

        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recipients
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="subtitle1">Recipient Groups</Typography>
              <FormControl fullWidth disabled={!isCampaignEditable}>
                <Select
                  multiple
                  value={campaign.groups || []}
                  onChange={(e) => handleInputChange('groups', e.target.value as string[])}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const group = groups?.find(g => g.id === value);
                        return group ? (
                          <Chip key={value} label={`${group.name} (${group.users_count} users)`} />
                        ) : null;
                      })}
                    </Box>
                  )}
                  displayEmpty
                >
                  {groups?.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name} ({group.users_count} users)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1">Individual Recipients</Typography>
              <UserAutocomplete
                value={campaign.individual_users || []}
                onChange={(users) => setCampaign((prev) => ({ ...prev, individual_users: users }))}
                label="Select Individual Users"
                placeholder="Search by name, username, or email"
              />
            </Grid>
            
            {errors.recipients && (
              <Grid item xs={12}>
                <FormHelperText error>{errors.recipients}</FormHelperText>
              </Grid>
            )}
          </Grid>
        </Box>

        <Divider />

        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Scheduling
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Schedule Date & Time"
                  value={scheduledDate}
                  onChange={(newValue) => setScheduledDate(newValue)}
                  disabled={!isCampaignEditable}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.scheduledDate,
                      helperText: errors.scheduledDate || '',
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                {!scheduledDate 
                  ? 'Leave empty to save as draft without scheduling' 
                  : `This notification will be sent on ${scheduledDate.toLocaleString()}`}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ p: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={isSubmitting && !sendNow ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={() => handleSubmit(false)}
            disabled={isSubmitting || !isCampaignEditable}
          >
            {isSubmitting && !sendNow ? 'Saving...' : 'Save as Draft'}
          </Button>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={isSubmitting && sendNow ? <CircularProgress size={20} /> : <SendIcon />}
            onClick={() => handleSubmit(true)}
            disabled={isSubmitting || !isCampaignEditable}
          >
            {isSubmitting && sendNow ? 'Scheduling...' : scheduledDate ? 'Schedule' : 'Schedule for Now'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default CampaignForm;
